import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';
import { getValueOf } from './helper';

const Container = styled.div`
  // defaults
  text-align: left;
  white-space: pre-wrap;
  font-size: 1.6rem;

  margin: ${getValueOf('my')} ${getValueOf('mx')};
  background: ${getValueOf('bg')};

  height: ${getValueOf('height')};
  max-height: ${getValueOf('maxHeight')};
  width: ${getValueOf('width')};

  // min width for preventing flex box shrinking
  min-width: ${getValueOf('width')};

  box-shadow: ${getValueOf('showShadow')};
  border-radius: 0.6rem;

  display: ${getValueOf('cardDisplay')};
  flex-direction: column;
`;

const Card = ({
  children,
  style,
  width,
  height,
  maxHeight,
  mx,
  my,
  bg,
  headerBg,
  headerPx,
  headerPy,
  bodyBg,
  bodyPx,
  bodyPy,
  footerBg,
  footerPx,
  footerPy,
  scroll,
  hideScroll,
  showShadow,
  justifyFooter,
  justifyHeader,
  inline,
  headerShadow,
  footerShadow,
}) => {
  return (
    <ThemeProvider
      theme={{
        mx,
        my,
        width,
        height,
        maxHeight,
        bg,
        headerBg,
        headerPx,
        headerPy,
        bodyBg,
        bodyPx,
        bodyPy,
        footerBg,
        footerPx,
        footerPy,
        hideScroll,
        scroll,
        showShadow,
        justifyFooter,
        justifyHeader,
        inline,
        headerShadow,
        footerShadow,
      }}
    >
      <Container style={style}>{children}</Container>
    </ThemeProvider>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  maxHeight: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.shape),
  mx: PropTypes.string,
  my: PropTypes.string,
  bg: PropTypes.string,
  headerBg: PropTypes.string,
  headerPx: PropTypes.string,
  headerPy: PropTypes.string,
  bodyBg: PropTypes.string,
  bodyPx: PropTypes.string,
  bodyPy: PropTypes.string,
  footerBg: PropTypes.string,
  footerPx: PropTypes.string,
  footerPy: PropTypes.string,
  scroll: PropTypes.bool,
  hideScroll: PropTypes.bool,
  showShadow: PropTypes.bool,
  justifyFooter: PropTypes.string,
  justifyHeader: PropTypes.string,
  inline: PropTypes.bool,
  headerShadow: PropTypes.bool,
  footerShadow: PropTypes.bool,
};
Card.defaultProps = {
  mx: '0',
  my: '0',
  style: {},
  width: 'auto',
  height: 'auto',
  maxHeight: 'auto',
  headerPx: '0',
  headerPy: '0',
  bodyPx: '0',
  bodyPy: '0',
  footerPx: '0',
  footerPy: '0',
  bg: 'none',
  headerBg: 'none',
  bodyBg: 'none',
  footerBg: 'none',
  scroll: true,
  hideScroll: false,
  showShadow: false,
  justifyFooter: 'flex-start',
  justifyHeader: 'flex-start',
  inline: false,
  headerShadow: false,
  footerShadow: false,
};

const Header = styled.div`
  background: ${getValueOf('headerBg')};
  padding: ${getValueOf('headerPy')} ${getValueOf('headerPx')};

  display: flex;
  justify-content: ${getValueOf('justifyHeader')};
  box-shadow: ${getValueOf('headerShadow')};
`;

Card.Header = ({ children, style }) => (
  <Header style={style}>{children}</Header>
);

Card.Header.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.objectOf(PropTypes.shape),
};
Card.Header.defaultProps = {
  style: {},
};

const Body = styled.div`
  background: ${getValueOf('bodyBg')};
  padding: ${getValueOf('bodyPy')} ${getValueOf('bodyPx')};
  flex-grow: 1;
  overflow-y: ${getValueOf('scroll')};
  &::-webkit-scrollbar {
    display: ${getValueOf('hideScroll')};
  }
`;

Card.Body = ({ children, style }) => <Body style={style}>{children}</Body>;

Card.Body.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.objectOf(PropTypes.shape),
};
Card.Body.defaultProps = {
  style: {},
};

const Footer = styled.div`
  background: none;
  padding: ${getValueOf('footerPy')} ${getValueOf('footerPx')};
  display: flex;
  justify-content: ${getValueOf('justifyFooter')};
  box-shadow: ${getValueOf('footerShadow')};
`;

Card.Footer = ({ children, style }) => (
  <Footer style={style}>{children}</Footer>
);

Card.Footer.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.objectOf(PropTypes.shape),
};
Card.Footer.defaultProps = {
  style: {},
};

export default Card;
